"use client";

import { useCallback, useState } from "react";
import { getUserTokensCookie } from "../edge/user-tokens-cookie";
import { hasRequiredUserTokens } from "./user-tokens-observer";

function readIdToken(cookie: Record<string, unknown> | null): string | null {
  return typeof cookie?.idToken === "string" ? cookie.idToken : null;
}

/**
 * Loop-safe recovery signal for the token escape-hatch pages (`/token-sync`,
 * `/token-refresh-error`). Middleware redirects there when the FELT_USER_TOKENS
 * cookie is rejected, and the cookie we land with may be structurally valid yet
 * dead. Recovery must mean the `UserTokensObserver` wrote a *different* idToken —
 * not the mere structural validity of the rejected one — or redirecting back
 * bounces off middleware in a ~1-2s loop (the render multiplier behind the Owlet
 * Stripe 429 storm).
 *
 * Captures the idToken at mount once (lazy init, client-only read) and returns a
 * predicate — `hasRecovered()` — that reports true only when the live cookie has
 * every required token AND a changed idToken. A cold start (no idToken at mount)
 * still recovers on the first valid token, since any token differs from the null
 * baseline.
 */
export function useTokenRecovery(): () => boolean {
  const [mountIdToken] = useState<string | null>(() =>
    readIdToken(getUserTokensCookie())
  );

  return useCallback(() => {
    const current = getUserTokensCookie();
    return (
      hasRequiredUserTokens(current) && readIdToken(current) !== mountIdToken
    );
  }, [mountIdToken]);
}
